import React from "react"
import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material"
import { dsColorNew } from '@palette'
import { dsRepricingDrawerData } from "Pages/DsRepricing/Page/@signals"
import { readPeriod, withPeriodField } from "Pages/DsRepricing/Page/@components/periodShape"
import { dsRepricingToggleSx } from "Pages/DsRepricing/Page/@components/toggleSx"

// ISO: 1 = Monday … 7 = Sunday. NOT the 0-based numbering the reports filter uses — the API
// validates 1..7 (`schemas.py:PeriodIn._weekday_range`) and the engine compares against
// `date.isoweekday()`. While this file was 0-based, picking Sat+Sun sent [5,6] and the backend
// stored Fri+Sat, and picking Monday sent 0 and the save failed with a 422.
const WEEKDAY_OPTIONS = [
	{ label: "Mon", full: "Monday",    value: 1 },
	{ label: "Tue", full: "Tuesday",   value: 2 },
	{ label: "Wed", full: "Wednesday", value: 3 },
	{ label: "Thu", full: "Thursday",  value: 4 },
	{ label: "Fri", full: "Friday",    value: 5 },
	{ label: "Sat", full: "Saturday",  value: 6 },
	{ label: "Sun", full: "Sunday",    value: 7 },
]

const WEEKDAY_LABEL = Object.fromEntries(WEEKDAY_OPTIONS.map(o => [o.value, o.full]))
export const weekdaysLabel = days => (days ?? []).map(d => WEEKDAY_LABEL[d] ?? d).join(', ')

// A row of 7 pill toggles — cheaper visually than an autocomplete for a
// closed set of 7 items, and consistent with the segmented control that
// picks the period mode above.
const pillsToggleSx = {
	...dsRepricingToggleSx,
	'& .MuiToggleButton-root': {
		...dsRepricingToggleSx['& .MuiToggleButton-root'],
		flex: 1,
	},
}

// A filter, not a mode: no selection means every day, so there is nothing here that can be
// invalid and nothing that should block Next.
export default function DsRepricingWeekdays() {
	const weekdays = readPeriod(dsRepricingDrawerData.value).weekdays

	const handleChange = (_, next) => {
		const days = [...(next ?? [])].sort((a, b) => a - b)
		dsRepricingDrawerData.value = withPeriodField(dsRepricingDrawerData.value, 'weekdays', days)
	}

	return (
		<Box>
			{/* "Pickup weekdays", never the bare word: the strategy has two
			    weekday selectors since CU-869fhuhy6 (criterion 32) — this one
			    is about the rentals priced, card 5's "Days it runs" is about
			    when the engine fires. */}
			<Typography sx={{ fontSize: 14, color: dsColorNew.textDark, fontWeight: 600 }}>Pickup weekdays (rentals picked up on…)</Typography>
			<Typography component="span" sx={{ fontSize: 12, color: dsColorNew.textMuted, display: 'block', mb: 1 }}>
				Optional. Leave every day unselected to price pickups on any day.
			</Typography>
			<ToggleButtonGroup value={weekdays} onChange={handleChange}
			                    size="small" sx={{ ...pillsToggleSx, width: '100%' }}>
				{WEEKDAY_OPTIONS.map(o => (
					<ToggleButton key={o.value} value={o.value} title={o.full}>
						{o.label}
					</ToggleButton>
				))}
			</ToggleButtonGroup>
			<Typography component="span" sx={{ fontSize: 12, color: dsColorNew.textMuted, display: 'block', mt: 0.75 }}>
				{weekdays.length
					? `Narrows the period above to pickups falling on ${weekdaysLabel(weekdays)}.`
					: 'The rule applies to every pickup inside the period above.'}
			</Typography>
		</Box>
	)
}
